import Button from 'common/components/Button';
import ErrorComponent from 'common/components/Error';
import { TIERS, bandIndexForCap, capLabel } from 'common/tiers';
import {
  DeveloperRequestPayload,
  RequestType,
} from 'common/types/developerRequests';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styles from './index.module.scss';

/** The first question. `request_type` is derived from the answers, not asked. */
type RequestCategory = 'LIMITS' | 'FEATURE_REQUEST' | 'SUPPORT_QUESTION';

const CATEGORY_OPTIONS: {
  value: RequestCategory;
  label: string;
  hint: string;
}[] = [
  {
    value: 'LIMITS',
    label: 'Request higher limits',
    hint: 'More WHOOP members, or more API calls per minute / per day',
  },
  {
    value: 'FEATURE_REQUEST',
    label: 'Idea / feature request',
    hint: 'Suggest something you would like the WHOOP API to support',
  },
  {
    value: 'SUPPORT_QUESTION',
    label: 'Support question',
    hint: 'Something is not working, or you have a question for the API team',
  },
];

/** Upper bounds on what the form will accept, not per-tier gates. */
const MAX_MINUTE_RATE_LIMIT = 1000;
const MAX_DAY_RATE_LIMIT = 50000;

const MIN_INTENDED_USE = 100;
const MIN_LIMITS_REASON = 120;
const MIN_IDEA_REQUEST = 30;
const MIN_QUESTION = 30;
const MIN_SHORT_REASON = 40;

interface FormValues {
  categoryType: RequestCategory;
  // LIMITS branch — capacity
  requestedTier: string;
  requestedMinuteRateLimit: number;
  requestedDayRateLimit: number;
  limitsReason: string;
  // LIMITS branch — about the app
  intendedUse: string;
  uxDesignsLink: string;
  publicUrl: string;
  noPublicUrl: boolean;
  noPublicUrlReason: string;
  // FEATURE_REQUEST branch
  ideaRequest: string;
  // SUPPORT_QUESTION branch
  question: string;
}

export interface RequestFormProps {
  appClientId?: string;
  /** Already launched to all members, so there is no cap to raise. */
  appApproved?: boolean;
  /** `null` means deliberately unlimited; `undefined` means we could not read it. */
  currentUserLimit?: number | null;
  currentUserCount?: number;
  currentMinuteLimit?: number;
  currentDayLimit?: number;
  loading?: boolean;
  error?: string;
  submitted?: boolean;
  onSubmit: (payload: DeveloperRequestPayload) => void;
  onCancel: () => void;
  /** Leaves the form entirely from the first step. */
  onBackFromStart?: () => void;
}

// High-level wizard steps. Branch-specific question steps live inside BRANCH.
type Step =
  | 'INTRO' // welcome + one-time reminder
  | 'COMMON' // request category
  | 'BRANCH'
  | 'SUBMITTED';

const fieldError = (msg?: string) =>
  msg ? <p className={styles.errorMessage}>{msg}</p> : null;

/** A number input left blank reads back as NaN via `valueAsNumber`. */
const provided = (n: number | undefined): boolean =>
  typeof n === 'number' && !Number.isNaN(n) && n > 0;

/** Reject answers that are only whitespace — `required` alone lets "   " through. */
const nonBlank = (v: string) => v.trim().length > 0 || 'This field is required';

const minLength = (v: string, min: number) =>
  v.trim().length >= min ||
  `Please give more detail — at least ${min} characters.`;

const wholeNumber = (v: number) =>
  Number.isInteger(v) || 'Enter a whole number.';

function RequestForm({
  appClientId,
  appApproved = false,
  currentUserLimit,
  currentUserCount,
  currentMinuteLimit,
  currentDayLimit,
  loading = false,
  error,
  submitted = false,
  onSubmit,
  onCancel,
  onBackFromStart,
}: RequestFormProps) {
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      categoryType: undefined,
      // Start on the band the app is already on, so the default reads as
      // "no change" rather than an unanswered question.
      requestedTier: TIERS[bandIndexForCap(currentUserLimit)]?.name ?? '',
      limitsReason: '',
      intendedUse: '',
      uxDesignsLink: '',
      publicUrl: '',
      noPublicUrl: false,
      noPublicUrlReason: '',
      ideaRequest: '',
      question: '',
    },
  });
  const {
    register,
    watch,
    trigger,
    getValues,
    setError,
    setValue,
    clearErrors,
    formState,
  } = methods;
  const { errors } = formState;

  const [step, setStep] = useState<Step>('INTRO');
  // Mirror `step` in a ref so the auto-advance timeout reads the latest value
  // (a closure would capture a stale step).
  const stepRef = useRef(step);
  useEffect(() => {
    stepRef.current = step;
  }, [step]);
  const categoryType = watch('categoryType');
  const noPublicUrl = watch('noPublicUrl');

  const currentBandIndex = bandIndexForCap(currentUserLimit);
  const currentBand =
    currentBandIndex >= 0 ? TIERS[currentBandIndex] : undefined;
  const nextBand =
    currentBandIndex >= 0 ? TIERS[currentBandIndex + 1] : undefined;
  const knowsCurrentBand = currentBandIndex >= 0;

  /**
   * The 50% gate on moving up a band. Applies from Build (100) upward — an app on a
   * 10-member cap has nothing to demonstrate. Undefined when the gate does not apply
   * or usage is unknown, since we do not block a request we cannot evaluate.
   */
  const nextBandGate = (():
    | { required: number; connected: number; met: boolean }
    | undefined => {
    // Everything is measured against the normalized tier, not the hand-set
    // limit: a 1,500-member app is treated as Growth, so it needs 500 to move
    // up — half of Growth's 1,000 — not half of its own 1,500.
    const bandCap = currentBand?.cap;
    if (!bandCap || bandCap < 100) return undefined;
    if (typeof currentUserCount !== 'number') return undefined;
    const required = Math.ceil(bandCap / 2);
    return {
      required,
      connected: currentUserCount,
      met: currentUserCount >= required,
    };
  })();

  const nextBandLocked = !!nextBandGate && !nextBandGate.met;

  // The tier question only renders when we know the current cap AND there is a
  // band above it. Three reasons it can be absent, each with its own answer.
  // Never render nothing: silently omitting it once made the whole tier ask
  // look missing.
  let tierUnavailableNote: string;
  if (appApproved || currentUserLimit === null) {
    // Both mean the same thing to a developer: no cap. Say it one way.
    tierUnavailableNote =
      'Your app has no member limit, so there is no cap to raise. You can still request higher API rate limits below.';
  } else if (currentUserLimit === undefined) {
    tierUnavailableNote =
      'We could not read your app’s member limit, so the tier question is unavailable. You can still request higher API rate limits below.';
  } else {
    tierUnavailableNote = `Your app is already on the highest band (${
      TIERS[bandIndexForCap(currentUserLimit)]?.name
    }). Further growth is handled as a partnership — email apisupport@whoop.com to start that conversation. You can still request higher API rate limits below.`;
  }

  const showTierQuestion = !appApproved && knowsCurrentBand && !!nextBand;

  const raised = (v: FormValues) => {
    // Only the band ABOVE the current one counts as asking for more members —
    // picking the current tier is an explicit "no change".
    const users =
      showTierQuestion && !!nextBand && v.requestedTier === nextBand.name;
    const rate =
      provided(v.requestedMinuteRateLimit) || provided(v.requestedDayRateLimit);
    return { users, rate };
  };

  const deriveRequestType = (v: FormValues): RequestType => {
    const { users, rate } = raised(v);
    if (users && rate) return 'APP_APPROVAL';
    if (users) return 'APP_LIMIT_INCREASE';
    return 'RATE_LIMIT_INCREASE';
  };

  const buildPayload = (): DeveloperRequestPayload => {
    const v = getValues();
    switch (v.categoryType) {
      case 'FEATURE_REQUEST':
        return { requestType: 'FEATURE_REQUEST', ideaRequest: v.ideaRequest };
      case 'SUPPORT_QUESTION':
        return { requestType: 'SUPPORT_QUESTION', question: v.question };
      case 'LIMITS': {
        const { users, rate } = raised(v);
        return {
          requestType: deriveRequestType(v),
          clientId: appClientId,
          intendedUse: v.intendedUse.trim(),
          uxDesignsLink: v.uxDesignsLink,
          ...(users
            ? {
                requestedUserLimit:
                  TIERS.find((t) => t.name === v.requestedTier)?.cap ??
                  undefined,
              }
            : {}),
          ...(provided(v.requestedMinuteRateLimit)
            ? { requestedMinuteRateLimit: v.requestedMinuteRateLimit }
            : {}),
          ...(provided(v.requestedDayRateLimit)
            ? { requestedDayRateLimit: v.requestedDayRateLimit }
            : {}),
          ...(noPublicUrl
            ? { noPublicUrlReason: v.noPublicUrlReason.trim() }
            : { publicUrl: v.publicUrl.trim() }),
          // One "why" answer, filed against whichever limit(s) they raised so the
          // existing admin columns stay meaningful.
          ...(rate ? { higherRateLimitReason: v.limitsReason } : {}),
          ...(users ? { higherAppLimitReason: v.limitsReason } : {}),
        };
      }
      default:
        throw new Error(`Unexpected category: ${v.categoryType}`);
    }
  };

  // Validate the fields belonging to the current step before advancing.
  const advanceFromCommon = async () => {
    const ok = await trigger(['categoryType']);
    if (!ok) return;
    setStep('BRANCH');
  };

  // Selecting a category auto-advances; the delay lets it visibly register.
  const handleCategorySelect = () => {
    setTimeout(() => {
      // Only advance if the user is still on the category step. If they pressed
      // Back during the delay, advancing would yank them onto BRANCH.
      if (stepRef.current === 'COMMON') {
        advanceFromCommon();
      }
    }, 200);
  };

  const limitsFields = (): (keyof FormValues)[] => {
    const fields: (keyof FormValues)[] = [
      'intendedUse',
      'uxDesignsLink',
      'limitsReason',
      'requestedMinuteRateLimit',
      'requestedDayRateLimit',
    ];
    fields.push(noPublicUrl ? 'noPublicUrlReason' : 'publicUrl');
    // Without this the tier validator never runs on Continue, so the
    // band-gating rules were enforced only by the disabled <option>s.
    if (showTierQuestion) {
      fields.push('requestedTier');
    }
    return fields;
  };

  const advanceFromBranch = async () => {
    let fields: (keyof FormValues)[] = [];
    switch (categoryType) {
      case 'LIMITS':
        fields = limitsFields();
        break;
      case 'FEATURE_REQUEST':
        fields = ['ideaRequest'];
        break;
      case 'SUPPORT_QUESTION':
        fields = ['question'];
        break;
      default:
        fields = [];
    }
    const ok = await trigger(fields);
    if (!ok) return;

    // Cross-field rule: a limits request that raises nothing is not a request.
    // Field-level `required` cannot express this, since each ask is individually
    // optional.
    if (categoryType === 'LIMITS') {
      const { users, rate } = raised(getValues());
      if (!users && !rate) {
        setError(
          showTierQuestion ? 'requestedTier' : 'requestedMinuteRateLimit',
          {
            type: 'manual',
            message: showTierQuestion
              ? 'Select the next tier, or enter a rate limit you need raised.'
              : 'Enter at least one rate limit you need raised.',
          },
        );
        return;
      }
    }

    onSubmit(buildPayload());
  };

  // After a successful submit, parent flips `submitted`.
  const effectiveStep: Step = submitted ? 'SUBMITTED' : step;

  // Label for the branch (final) step's primary button.
  const branchPrimaryLabel = loading ? 'Submitting...' : 'Submit';

  const renderTextArea = (
    name: keyof FormValues,
    label: string,
    min: number,
    placeholder: string,
    hint?: string,
  ) => (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={name}>
        <span>
          {label}
          <span className={styles.required}>*</span>
        </span>
        <textarea
          id={name}
          data-testid={`field-${name}`}
          className={`${styles.formInput} ${styles.textArea} ${
            errors[name] ? styles.inputError : ''
          }`.trim()}
          placeholder={placeholder}
          {...register(name, {
            required: 'This field is required',
            validate: {
              nonBlank: (v) => nonBlank(v as string),
              minLength: (v) => minLength(v as string, min),
            },
          })}
        />
      </label>
      {hint && <p className={styles.fieldHint}>{hint}</p>}
      {fieldError(errors[name]?.message as string | undefined)}
    </div>
  );

  const renderNumberField = (
    name: keyof FormValues,
    label: string,
    max: number,
    placeholder: string,
    required: boolean,
    hint?: React.ReactNode,
    floor?: number,
  ) => (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={name}>
        <span>
          {label}
          {required && <span className={styles.required}>*</span>}
        </span>
        <input
          type="number"
          min={1}
          step={1}
          id={name}
          data-testid={`field-${name}`}
          className={`${styles.formInput} ${
            errors[name] ? styles.inputError : ''
          }`.trim()}
          placeholder={placeholder}
          {...register(name, {
            valueAsNumber: true,
            ...(required ? { required: 'This field is required' } : {}),
            validate: {
              present: (v) =>
                required
                  ? provided(v as number) || 'This field is required'
                  : true,
              whole: (v) => !provided(v as number) || wholeNumber(v as number),
              // Asking for the limit you already have is not a request.
              floor: (v) =>
                !provided(v as number) ||
                floor === undefined ||
                (v as number) > floor ||
                `You already have ${floor.toLocaleString()}. Enter a higher number, or leave this blank.`,
              ceiling: (v) =>
                !provided(v as number) ||
                (v as number) <= max ||
                `Above ${max.toLocaleString()} is reviewed as a production partnership — contact the API team rather than filing this form.`,
            },
          })}
        />
      </label>
      {hint && <p className={styles.fieldHint}>{hint}</p>}
      {fieldError(errors[name]?.message as string | undefined)}
    </div>
  );

  const renderTierQuestion = () => {
    const cap = currentUserLimit as number;
    const onStandardCap = currentBand?.cap === cap;
    // The 50%-of-capacity trigger only bites from Build (100) upward. Below that
    // an app may apply straight away — there is nothing to prove at 10 members.
    const usageRuleApplies = (currentBand?.cap ?? 0) >= 100;

    let nextBandNote: React.ReactNode = (
      <>Your app is already on the highest band.</>
    );
    if (nextBand && nextBandLocked) {
      nextBandNote = (
        <>
          <b>{nextBand.name}</b> (up to {capLabel(nextBand.cap)} members)
          unlocks once you have{' '}
          <b>{nextBandGate!.required.toLocaleString()} connected members</b>{' '}
          &mdash; 50% of the {currentBand?.name} cap. You have{' '}
          <b>{nextBandGate!.connected.toLocaleString()}</b> today, so there is
          nothing to request yet. You can still ask for higher rate limits
          below.
        </>
      );
    } else if (nextBand) {
      const nextGateSize = Math.ceil((nextBand.cap ?? 0) / 2);
      // Three cases, and the gate object is absent in two of them: it is only
      // built once the cap is 100+ AND we know the connected count.
      let ruleTail: string;
      if (nextBandGate) {
        ruleTail = `, which is ${nextBandGate.required.toLocaleString()} at ${
          currentBand?.name
        } — you have ${nextBandGate.connected.toLocaleString()}.`;
      } else if (usageRuleApplies) {
        ruleTail = `, which is ${Math.ceil(
          (currentBand?.cap ?? 0) / 2,
        ).toLocaleString()} at ${currentBand?.name}.`;
      } else {
        ruleTail = `. That rule starts at ${TIERS[1].name}: leaving ${nextBand.name} will need ${nextGateSize} connected members.`;
      }
      nextBandNote = (
        <>
          You can apply for <b>{nextBand.name}</b> (up to{' '}
          {capLabel(nextBand.cap)} members), one band at a time. Moving up a
          tier needs <b>50% of your tier&apos;s member cap connected</b>
          {ruleTail}
        </>
      );
    }
    return (
      <div className={styles.field}>
        <label className={styles.label} htmlFor="requestedTier">
          <span>How many WHOOP members do you need access for?</span>
          <select
            id="requestedTier"
            data-testid="field-requestedTier"
            className={`${styles.selectInput} ${
              errors.requestedTier ? styles.inputError : ''
            }`.trim()}
            {...register('requestedTier', {
              // The disabled <option>s are only a UI affordance — enforce both
              // rules here too: stay put or move one band up, and the band
              // above is gated on connected members.
              validate: (v) => {
                const picked = v as string;
                const allowed = [currentBand?.name, nextBand?.name].filter(
                  Boolean,
                );
                if (picked !== '' && !allowed.includes(picked)) {
                  return `You can only stay on ${currentBand?.name} or move up to ${nextBand?.name}.`;
                }
                if (nextBandLocked && picked === nextBand?.name) {
                  return `${
                    nextBand?.name
                  } needs ${nextBandGate!.required.toLocaleString()} connected members. You have ${nextBandGate!.connected.toLocaleString()}.`;
                }
                return true;
              },
            })}
          >
            {TIERS.map((tier, i) => {
              if (i === currentBandIndex) {
                return (
                  <option key={tier.name} value={tier.name}>
                    {tier.name} &mdash; {capLabel(tier.cap)} members (your
                    current tier, no change)
                  </option>
                );
              }
              if (i === currentBandIndex + 1) {
                return (
                  <option
                    key={tier.name}
                    value={tier.name}
                    disabled={nextBandLocked}
                  >
                    {tier.name} &mdash; {capLabel(tier.cap)} members
                    {nextBandLocked
                      ? ` (reach ${nextBandGate!.required.toLocaleString()} connected members first)`
                      : ''}
                  </option>
                );
              }
              // Bands below the current one are behind us; bands two or more
              // above are gated on reaching the one in between. Guarding the
              // index matters now that lower bands reach this branch too.
              const gate = TIERS[i - 1];
              let disabledReason = '';
              if (i < currentBandIndex) {
                disabledReason = ' (below your current tier)';
              } else if (gate) {
                disabledReason = ` (move up through ${gate.name} first)`;
              }
              return (
                <option key={tier.name} value={tier.name} disabled>
                  {tier.name} &mdash; {capLabel(tier.cap)} members
                  {disabledReason}
                </option>
              );
            })}
          </select>
        </label>
        <p className={styles.fieldHint} data-testid="tier-disclaimer">
          Your current limit is <b>{cap.toLocaleString()} members</b>.
          {!onStandardCap && (
            <>
              {' '}
              Limits that fall between tiers round down, so you are treated as{' '}
              <b>{currentBand?.name}</b>.
            </>
          )}{' '}
          {nextBandNote}
        </p>
        {fieldError(errors.requestedTier?.message)}
      </div>
    );
  };

  const renderLimitsBranch = () => (
    <>
      <p className={styles.stepHelper}>
        Tell us the limits you need. Leave anything you do not need blank.
      </p>

      {showTierQuestion ? (
        renderTierQuestion()
      ) : (
        // Never drop the member question silently: if it is unavailable, say why.
        // Silently omitting it once made the whole tier ask look missing.
        <p className={styles.fieldHint} data-testid="tier-unavailable">
          {tierUnavailableNote}
        </p>
      )}

      {renderNumberField(
        'requestedMinuteRateLimit',
        'API calls per minute',
        MAX_MINUTE_RATE_LIMIT,
        'e.g. 200',
        false,
        currentMinuteLimit !== undefined ? (
          <span data-testid="current-minute-limit">
            Your current limit is <b>{currentMinuteLimit.toLocaleString()}</b>{' '}
            calls per minute.
          </span>
        ) : undefined,
        currentMinuteLimit,
      )}
      {renderNumberField(
        'requestedDayRateLimit',
        'API calls per day',
        MAX_DAY_RATE_LIMIT,
        'e.g. 20000',
        false,
        currentDayLimit !== undefined ? (
          <span data-testid="current-day-limit">
            Your current limit is <b>{currentDayLimit.toLocaleString()}</b>{' '}
            calls per day.
          </span>
        ) : undefined,
        currentDayLimit,
      )}
      {renderTextArea(
        'limitsReason',
        'Why do you need these higher limits?',
        MIN_LIMITS_REASON,
        'Explain what your current limits are blocking in your app. Be specific, and include the usage you are seeing from your userbase',
        'Explain in detailed, specific terms. Vague answers will be rejected — tell us what is blocked today, for whom, and what changes if the limit is raised.',
      )}

      {renderTextArea(
        'intendedUse',
        'What does your app do, and how does it use WHOOP data?',
        MIN_INTENDED_USE,
        'Describe how your app uses WHOOP data and what members get from it',
      )}

      <div className={styles.field}>
        <label className={styles.label} htmlFor="uxDesignsLink">
          <span>
            Please share a link to mockups or screenshots of your WHOOP
            integration.
            <span className={styles.required}>*</span>
          </span>
          <input
            type="text"
            id="uxDesignsLink"
            data-testid="field-uxDesignsLink"
            className={`${styles.formInput} ${
              errors.uxDesignsLink ? styles.inputError : ''
            }`.trim()}
            placeholder="https://..."
            {...register('uxDesignsLink', {
              required: 'This field is required',
              pattern: {
                // http and https both accepted; requires a host with a dot, so
                // "https://x" does not pass.
                value: /^https?:\/\/[^\s/]+\.[^\s/]+/,
                message:
                  'Please enter a valid URL, starting with http:// or https://',
              },
            })}
          />
        </label>
        {fieldError(errors.uxDesignsLink?.message)}
      </div>

      <div className={styles.field}>
        <label className={styles.label} htmlFor="publicUrl">
          <span>
            What is your app&apos;s public URL or App Store page?
            {!noPublicUrl && <span className={styles.required}>*</span>}
          </span>
          <input
            type="text"
            id="publicUrl"
            data-testid="field-publicUrl"
            disabled={noPublicUrl}
            className={`${styles.formInput} ${
              errors.publicUrl ? styles.inputError : ''
            }`.trim()}
            placeholder="https://yourapp.com or App Store link"
            {...register('publicUrl', {
              validate: (v) => {
                if (noPublicUrl) return true;
                const s = ((v as string) || '').trim();
                if (s === '') return 'This field is required';
                return (
                  /^https?:\/\/[^\s/]+\.[^\s/]+/.test(s) ||
                  'Please enter a valid URL, starting with http:// or https://'
                );
              },
            })}
          />
        </label>
        <label className={styles.checkboxRow} htmlFor="noPublicUrl">
          <input
            type="checkbox"
            id="noPublicUrl"
            data-testid="field-noPublicUrl"
            {...register('noPublicUrl', {
              // The two answers are mutually exclusive, so empty whichever one
              // no longer applies. Without this a URL typed before ticking the
              // box stays in form state and shows through the disabled input.
              // Clearing the errors too stops a stale "This field is required"
              // sitting under the URL after the box is ticked.
              onChange: (e) => {
                clearErrors(['publicUrl', 'noPublicUrlReason']);
                if (e.target.checked) {
                  setValue('publicUrl', '');
                } else {
                  setValue('noPublicUrlReason', '');
                }
              },
            })}
          />
          <span>My app has neither a public URL nor an App Store page</span>
        </label>
        {fieldError(errors.publicUrl?.message)}
        {noPublicUrl && (
          <div className={styles.conditional}>
            <label className={styles.label} htmlFor="noPublicUrlReason">
              <span>
                Why does your app have neither?
                <span className={styles.required}>*</span>
              </span>
              <textarea
                id="noPublicUrlReason"
                data-testid="field-noPublicUrlReason"
                className={`${styles.formInput} ${styles.textArea} ${
                  errors.noPublicUrlReason ? styles.inputError : ''
                }`.trim()}
                {...register('noPublicUrlReason', {
                  validate: (v) => {
                    if (!noPublicUrl) return true;
                    const s = ((v as string) || '').trim();
                    return (
                      s.length >= MIN_SHORT_REASON ||
                      `Please explain — at least ${MIN_SHORT_REASON} characters.`
                    );
                  },
                })}
              />
            </label>
            {fieldError(errors.noPublicUrlReason?.message)}
          </div>
        )}
      </div>
    </>
  );

  const renderBranchStep = () => {
    switch (categoryType) {
      case 'LIMITS':
        return renderLimitsBranch();
      case 'FEATURE_REQUEST':
        return renderTextArea(
          'ideaRequest',
          'What is your idea / request?',
          MIN_IDEA_REQUEST,
          'Describe your idea or feature request',
        );
      case 'SUPPORT_QUESTION':
        return renderTextArea(
          'question',
          'What is your question?',
          MIN_QUESTION,
          'Ask your question in as much detail as you can',
        );
      default:
        return null;
    }
  };

  // ---- Screens ----
  if (effectiveStep === 'SUBMITTED') {
    return (
      <div className={styles.root} data-testid="request-submitted">
        <h2 className={styles.stepTitle}>Thank you</h2>
        <p className={styles.intro}>
          Your request has been submitted. We will contact you regarding your
          request at the email address associated with your WHOOP account as
          soon as we are able. We appreciate your patience.
        </p>
        <div className={styles.nav}>
          <Button
            variant="primary"
            theme="dark"
            onClick={onCancel}
            data-testid="done-button"
          >
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div key={step} className={styles.stepContainer}>
        {effectiveStep === 'INTRO' && (
          <div data-testid="step-intro">
            <p className={styles.intro}>
              Use this form to submit requests to the WHOOP API Platform team.
            </p>
            <p className={styles.intro}>
              Requests are reviewed manually, in the order they are received.
              Requests without specific, detailed answers will be rejected. You
              will get an email once your request has been approved or rejected.
            </p>
            <p className={styles.intro}>
              Contacting WHOOP employees about your request by personal email,
              social, or any other channel outside this form is strictly
              prohibited and may result in your app being disabled.
              Additionally, WHOOP Membership Services cannot provide status
              updates on API requests.
            </p>
            <div className={styles.nav}>
              <Button
                variant="normal"
                theme="dark"
                onClick={() => onBackFromStart?.()}
                data-testid="intro-back"
              >
                Back
              </Button>
              <Button
                variant="primary"
                theme="dark"
                onClick={() => setStep('COMMON')}
                data-testid="intro-continue"
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {effectiveStep === 'COMMON' && (
          <div data-testid="step-common">
            <div className={styles.field}>
              <span className={styles.label} id="category-label">
                <span>
                  What type of request is this?
                  <span className={styles.required}>*</span>
                </span>
              </span>
              <div
                className={styles.choiceList}
                role="radiogroup"
                aria-labelledby="category-label"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    htmlFor={`request-type-input-${opt.value}`}
                    className={`${styles.choice} ${
                      categoryType === opt.value ? styles.choiceSelected : ''
                    }`.trim()}
                    data-testid={`request-type-${opt.value}`}
                  >
                    <input
                      type="radio"
                      id={`request-type-input-${opt.value}`}
                      value={opt.value}
                      {...register('categoryType', {
                        required: 'Please select a request type',
                        onChange: handleCategorySelect,
                      })}
                    />
                    <span>
                      {opt.label}
                      <span className={styles.choiceHint}>{opt.hint}</span>
                    </span>
                  </label>
                ))}
              </div>
              {fieldError(errors.categoryType?.message)}
            </div>

            <div className={styles.nav}>
              <Button
                variant="normal"
                theme="dark"
                onClick={() => setStep('INTRO')}
                data-testid="common-back"
              >
                Back
              </Button>
            </div>
          </div>
        )}

        {effectiveStep === 'BRANCH' && (
          <div data-testid="step-branch">
            {renderBranchStep()}
            {error && <ErrorComponent>{error}</ErrorComponent>}
            <div className={styles.nav}>
              <Button
                variant="normal"
                theme="dark"
                onClick={() => setStep('COMMON')}
                data-testid="branch-back"
              >
                Back
              </Button>
              <Button
                variant="primary"
                theme="dark"
                disabled={loading}
                onClick={advanceFromBranch}
                data-testid="branch-continue"
              >
                {branchPrimaryLabel}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RequestForm;
